import React from 'react';

/**
 * URL pattern to match:
 * - http:// or https:// URLs
 * - www. URLs (without protocol)
 * - URLs with paths, query strings, and fragments
 */
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>[\]{}|\\^`"']+(?:[.,!?;:)])?(?<![.,!?;:])/gi;

/**
 * Normalize URL to ensure it has a protocol
 * @param {string} url - The URL to normalize
 * @returns {string} URL with protocol
 */
const normalizeUrl = (url) => {
  if (url.startsWith('www.')) {
    return `https://${url}`;
  }
  return url;
};

/**
 * Check if character is trailing punctuation that shouldn't be part of URL
 * @param {string} char - Character to check
 * @returns {boolean}
 */
const isTrailingPunctuation = (char) => {
  return ['.', ',', '!', '?', ';', ':', ')', ']', '}', "'", '"'].includes(char);
};

/**
 * Clean URL by removing trailing punctuation
 * @param {string} url - URL to clean
 * @returns {string} Cleaned URL
 */
const cleanUrl = (url) => {
  let cleaned = url;
  while (cleaned.length > 0 && isTrailingPunctuation(cleaned[cleaned.length - 1])) {
    cleaned = cleaned.slice(0, -1);
  }
  return cleaned;
};

/**
 * Convert URLs in text to clickable hyperlinks
 * Returns an array of React elements (strings and anchor tags)
 * @param {string} text - The text to linkify
 * @returns {Array<React.ReactNode>} Array of text and link elements
 */
export const linkifyText = (text) => {
  if (!text || typeof text !== 'string') {
    return [text];
  }

  const parts = [];
  let lastIndex = 0;
  let match;

  // Reset regex lastIndex for global matching
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const url = cleanUrl(match[0]);
    const matchStart = match.index;
    const matchEnd = matchStart + url.length;

    // Add text before the URL
    if (matchStart > lastIndex) {
      parts.push(text.slice(lastIndex, matchStart));
    }

    // Add the URL as a link
    const href = normalizeUrl(url);
    parts.push(
      <a
        key={`link-${matchStart}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="chat-message-link"
      >
        {url}
      </a>
    );

    // Add any trailing punctuation that was cleaned
    const trailingPunctuation = match[0].slice(url.length);
    if (trailingPunctuation) {
      parts.push(trailingPunctuation);
    }

    lastIndex = matchEnd + trailingPunctuation.length;
  }

  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no URLs found, return original text in array
  if (parts.length === 0) {
    return [text];
  }

  return parts;
};

export default linkifyText;
